import type { Connection as ApiConnection, ConnectionType as ApiConnectionType } from '@datarecs/data-models-core-api';

/**
 * The shared ConnectionType, re-exported from `@datarecs/data-models-core-api` so console
 * code and package DTOs use one (nominally identical) enum type — never a local duplicate.
 *
 * Why not `export { ConnectionType } from '@datarecs/data-models-core-api'`? A value
 * re-export would drag the package's server-side module graph (NestJS decorators →
 * Node built-ins) into the browser bundle, and the enum is not part of the package's
 * browser-safe `/browser` entrypoint. So the type is re-exported as a type, and the
 * runtime members below are a browser-safe const mirror whose every member is typed as
 * the corresponding package enum member:
 *
 * - values flow into package-typed APIs with full nominal enum compatibility;
 * - a renamed/removed member upstream is a compile error right here;
 * - members ADDED upstream (e.g. SNOWFLAKE) do NOT auto-appear in the UI — surfaces
 *   like the connection-wizard tiles opt in explicitly, and icon/label maps fall back
 *   for unknown members.
 *
 * See docs/data-models-imports.md for the import-type rule this pattern satisfies.
 */
export type ConnectionType = ApiConnectionType;
export const ConnectionType = {
  POSTGRES: 'postgres' as ApiConnectionType.POSTGRES,
  MYSQL: 'mysql' as ApiConnectionType.MYSQL,
  DB2: 'db2' as ApiConnectionType.DB2,
  ORACLE: 'oracle' as ApiConnectionType.ORACLE,
  MSSQL: 'mssql' as ApiConnectionType.MSSQL,
  BIGQUERY: 'bigquery' as ApiConnectionType.BIGQUERY,
  GOOGLE_SHEETS: 'google_sheets' as ApiConnectionType.GOOGLE_SHEETS,
  TABLEAU: 'tableau' as ApiConnectionType.TABLEAU,
  LOOKER: 'looker' as ApiConnectionType.LOOKER,
  EXCEL_SHAREPOINT: 'excel_sharepoint' as ApiConnectionType.EXCEL_SHAREPOINT,
  SNOWFLAKE: 'snowflake' as ApiConnectionType.SNOWFLAKE,
  DYNAMODB: 'dynamodb' as ApiConnectionType.DYNAMODB
} as const;

/**
 * Browser-safe mirror of the upstream `DYNAMODB_SYNTHETIC_SCHEMA` constant (same value-import
 * constraint as the enum mirror above). DynamoDB has no schema layer, so every surface renders
 * this single synthetic schema between region and tables. The `typeof import(...)` annotation is
 * type-only (erased at runtime) and pins this mirror to the upstream literal — a changed value
 * upstream is a compile error here; `connection-type-mirror.spec.ts` asserts runtime equality too.
 */
export const DYNAMODB_SYNTHETIC_SCHEMA: typeof import('@datarecs/data-models-core-api').DYNAMODB_SYNTHETIC_SCHEMA = 'tables';

export type ConnectionSummary = Pick<ApiConnection, 'id' | 'name' | 'description' | 'type' | 'workspace_id' | 'created_at' | 'updated_at'> & {
  status?: 'active' | 'inactive' | 'error' | 'testing' | string;
  lastUsed?: Date;
  consent_status?: 'active' | 'revoked';
};

export type ConnectionsState = {
  connections: ConnectionSummary[];
  selectedConnection: ConnectionSummary | null;
  isLoading: boolean;
  error: string | null;
};

export type Connection = ApiConnection & {
  credentials?: Record<string, any>;
};
